import styles from './style.module.scss';
import { createRoot } from 'react-dom/client';
import Callout, { CalloutType, FavouriteDelegate } from './Callout';
import { ClusterAnnotationData } from '../ClusterAnnotationData';
import { MapPageListing } from '@zoocasa/go-search';
import { CompliancyDelegateType } from 'components/map-listing-card';
import { I18nextProvider } from 'react-i18next';
import { i18n } from 'next-i18next';
import { createCalloutAnchor, positionCalloutContent } from '../callout-positioning';

//#region Constants
const CALCULATED_CARD_HEIGHT = 140;
const CARD_MARGIN = 8;
const CALCULATED_POPOVER_HEADER_HEIGHT = 45;
const CALLOUT_ROOT_CLASS_NAME = styles['callout-root'];
//#endregion

//#region Helper Functions
const handleWheelEvent= (event: WheelEvent) => {
  event.stopImmediatePropagation(); // Prevent scrolling inside the callout from zooming the map
};

const getPopoverHeight = (count: number) => {

  let popoverHeight = CALCULATED_CARD_HEIGHT * (count < 4 ? count : 3);

  if (count === 1) {
    popoverHeight += 3 * CARD_MARGIN;
  } else if (count === 2) {
    popoverHeight += 4 * CARD_MARGIN;
  } else {
    popoverHeight += 6 * CARD_MARGIN;
  }
  return popoverHeight;
};
//#endregion

/** 
 * Creates a new instance of a mapkit.AnnotationCalloutDelegate that can be used for customizing 
 * the behavior and appearance of the Cluster annotation callout.
 * 
 * @param type - The type of callout to be displayed
 * @param compliancyDelegate - Delegate for handling compliancy-related functionality
 * @param favouriteDelegate - Delegate for handling favorite-related functionality
 * @param onListingClick - Callback function triggered when a listing is clicked
 * @param onListingAddressClick - Callback function triggered when a listing's address is clicked
 * @param onTermsRequiredClick - Callback for terms-of-use-required listings; routed
 *   separately from `onListingClick` so the parent (which lives in the app React tree
 *   with `ModalContext`) can open the terms modal. Required because this callout is
 *   rendered into its own `createRoot` tree without the app-level providers.
 * @param calloutShouldAppearForAnnotation - Function that determines if a callout should appear for a given annotation
 * @param isExpTheme - Flag indicating whether experimental theme is enabled
 * @param cardClassName - Custom class passed through to each MapListingCard
 * @returns A configured mapkit.AnnotationCalloutDelegate instance
 */
export default function createNewCalloutDelegate(
  type: CalloutType, 
  compliancyDelegate: CompliancyDelegateType,
  favouriteDelegate: FavouriteDelegate,
  onListingClick: (listing: MapPageListing) => void,
  onListingAddressClick: (listing: MapPageListing) => void,
  onTermsRequiredClick: (listing: MapPageListing) => void,
  calloutShouldAppearForAnnotation: (annotation: mapkit.Annotation) => boolean,
  isExpTheme: boolean,
  cardClassName?: string
) {
  const calloutDelegate: mapkit.AnnotationCalloutDelegate = {
    calloutElementForAnnotation: function(annotation: mapkit.Annotation) {
      const div = document.createElement('div');
      div.className = CALLOUT_ROOT_CLASS_NAME;
      const { listings, count } = annotation.data as ClusterAnnotationData;
      const popoverHeight = getPopoverHeight(count);
      div.style.height = `${popoverHeight + CALCULATED_POPOVER_HEADER_HEIGHT}px`;
      div.addEventListener('wheel', handleWheelEvent);
      
      const handleListingClicked = (listing: MapPageListing) => { 
        annotation.selected = false;
        onListingClick(listing); 
      };
      const handleListingAddressClicked = (listing: MapPageListing) => { 
        annotation.selected = false;
        onListingAddressClick(listing); 
      };
      const handleTermsRequiredClicked = (listing: MapPageListing) => {
        annotation.selected = false;
        onTermsRequiredClick(listing);
      };
      const handleCloseClicked = () => { 
        annotation.selected = false;
      };
      const container = createRoot(div);
      container.render(
        <I18nextProvider i18n={i18n}>
          <Callout 
            type={type}
            listings={listings}
            maxHeight={`${popoverHeight}px`}
            compliancyDelegate={compliancyDelegate}
            favouriteDelegate={favouriteDelegate} 
            onCloseClick={handleCloseClicked}
            onListingClick={handleListingClicked}
            onListingAddressClick={handleListingAddressClicked}
            onTermsRequiredClick={handleTermsRequiredClicked}
            isExpTheme={isExpTheme}
            cardClassName={cardClassName}
          />
        </I18nextProvider>
      );

      // Zero-size anchor: whenever this callout shows, MapKit must not pan the
      // map to fit it. Clusters too big for a callout still recenter via the
      // explicit zoom in Map.tsx's annotation select handler.
      const anchor = createCalloutAnchor(div);
      positionCalloutContent(div, annotation);
      return anchor;
    },
    calloutShouldAppearForAnnotation,
  };

  return calloutDelegate;
}
